import React, { useEffect } from 'react';
import { Outlet, useParams, Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getClubBySlug } from '../services/api';
import LoadingSpinner from '../components/shared/LoadingSpinner';

const ClubLayout = () => {
    const { slug } = useParams();
    const location = useLocation();

    // 1. Fetch Club Data
    const { data: response, isLoading, isError, error } = useQuery({
        queryKey: ['club', slug],
        queryFn: () => getClubBySlug(slug),
        staleTime: 5 * 60 * 1000,
        retry: 1,
    });

    const club = response;

    // 2. Inject Theme Colors
    useEffect(() => {
        if (club) {
            const root = document.documentElement;
            // Assuming club has primaryColor (e.g., #FF5733) and secondaryColor
            if (club.primaryColor) {
                root.style.setProperty('--color-primary-500', club.primaryColor);
                // Simple lighten/darken logic or just base color
            }
            // Add more variable overrides if needed
            document.title = `${club.name} - VisioPadel`;
        }

        // Cleanup function to reset colors when leaving layout
        return () => {
            // Optional: Reset to variables if we want to restore original theme
            // window.location.reload(); // Might be too aggressive. 
            // Better to just rely on CSS var scope if this was limited to a container, but it's :root
        };
    }, [club]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <LoadingSpinner size="lg" text="Cargando espacio del club..." />
            </div>
        );
    }

    if (isError || !club) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4 text-center">
                <h1 className="text-4xl font-bold text-gray-800 mb-4">Club no encontrado</h1>
                <p className="text-gray-600 mb-8">El espacio que buscas no existe o no está disponible.</p>
                <Link to="/" className="px-6 py-3 bg-primary-500 text-white rounded-lg font-bold">
                    Ir a VisioPadel Principal
                </Link>
            </div>
        );
    }

    // 3. Simple Club Navbar
    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
            {/* Club Navbar */}
            <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between h-20 gap-2">
                        {/* Club Logo */}
                        <Link to={`/c/${slug}`} className="flex items-center gap-2 md:gap-3 min-w-0">
                            {club.logoUrl ? (
                                <img
                                    src={club.logoUrl}
                                    alt={club.name}
                                    className="h-10 md:h-12 w-auto object-contain flex-shrink-0"
                                />
                            ) : (
                                <div className="h-8 w-8 md:h-10 md:w-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-700 font-bold text-lg md:text-xl flex-shrink-0">
                                    {club.name.charAt(0)}
                                </div>
                            )}
                            <span className="font-bold text-base md:text-xl text-gray-900 truncate max-w-[150px] md:max-w-md">
                                {club.name}
                            </span>
                        </Link>

                        {/* Actions */}
                        <div className="flex items-center gap-3 md:gap-4 flex-shrink-0">
                            <Link
                                to={`/c/${slug}/events`}
                                className={`font-bold text-[13px] md:text-sm ${location.pathname.includes('/events') ? 'text-primary-600' : 'text-gray-600 hover:text-primary-600'}`}
                            >
                                POZOS
                            </Link>

                            {/* "Powered by" small badge on desktop */}
                            <Link to="/" className="hidden sm:flex items-center gap-1 opacity-50 hover:opacity-100 transition-opacity">
                                <span className="text-[8px] md:text-[10px] uppercase font-bold text-gray-400">Powered by</span>
                                <img src="/visiopadel-logo.png" alt="VisioPadel" className="h-4 md:h-5" />
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Club Content (Landing or Events) */}
            <main className="flex-grow">
                {/* We pass club data via context to Outlet if needed, but Outlet context is cleaner */}
                <Outlet context={{ club }} />
            </main>

            {/* Simple Footer */}
            <footer className="bg-white border-t border-gray-100 py-8 text-center text-sm text-gray-500">
                <p>© {new Date().getFullYear()} {club.name}</p>
                <div className="mt-2 flex items-center justify-center gap-2">
                    <span>Gestionado con</span>
                    <Link to="/" className="font-bold text-primary-600 hover:text-primary-700">VisioPadel</Link>
                </div>
            </footer>
        </div>
    );
};

export default ClubLayout;
